<template>
  <div class="global-container">
    <div class="content">
      <!-- Header Section -->
      <div class="header-section">
        <div class="title-section">
          <h1 class="page-title">Cash & Bank</h1>
          <p class="page-description">Manage cash and bank accounts</p>
        </div>
        <button class="add-account-button" @click="openModal">
          <span class="plus-icon">+</span>
          Add Account
        </button>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid">
        <div class="stat-card total-balance">
          <div class="stat-header">
            <span class="stat-label">Total Balance</span>
            <span class="stat-icon">&#x1F4C8;</span>
          </div>
          <div class="stat-value">{{ formatCurrency(stats.totalBalance) }}</div>
        </div>

        <div class="stat-card cash-balance">
          <div class="stat-header">
            <span class="stat-label">Cash Balance</span>
            <span class="stat-icon">&#x1F4B5;</span>
          </div>
          <div class="stat-value">{{ formatCurrency(stats.cashBalance) }}</div>
        </div>

        <div class="stat-card bank-balance">
          <div class="stat-header">
            <span class="stat-label">Bank Balance</span>
            <span class="stat-icon">&#x1F3E6;</span>
          </div>
          <div class="stat-value">{{ formatCurrency(stats.bankBalance) }}</div>
        </div>

        <div class="stat-card total-accounts">
          <div class="stat-header">
            <span class="stat-label">Total Accounts</span>
            <span class="stat-icon">&#x1F4CA;</span>
          </div>
          <div class="stat-value">{{ stats.totalAccounts }}</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-section">
        <button
          v-for="tab in ['Accounts', 'Transactions']"
          :key="tab"
          class="tab-button"
          :class="{ active: activeTab === tab }"
          @click="activeTab = tab"
        >
          {{ tab }}
        </button>
      </div>

      <!-- Search and Filter -->
      <div class="search-filter-section">
        <div class="search-box">
          <span class="search-icon">&#x1F50D;</span>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search accounts..."
            class="search-input"
            @input="handleSearch"
          />
        </div>
        <div class="filter-dropdown">
          <select v-model="selectedFilter" class="filter-select" @change="handleFilterChange">
            <option value="">All Types</option>
            <option value="Cash">Cash</option>
            <option value="Bank">Bank</option>
          </select>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading accounts...</p>
      </div>

      <!-- Accounts Tab Content -->
      <div v-else-if="activeTab === 'Accounts'" class="accounts-section">
        <div class="accounts-header">Accounts</div>

        <!-- Desktop Table View -->
        <div v-if="!isMobileView" class="accounts-table">
          <div class="table-header">
            <div class="col-account-name">Account Name</div>
            <div class="col-type">Type</div>
            <div class="col-bank-no">Bank / Account No.</div>
            <div class="col-balance">Balance</div>
            <div class="col-status">Status</div>
            <div class="col-actions">Actions</div>
          </div>

          <div class="table-body">
            <div v-if="accounts.length === 0" class="empty-state">
              <p>No accounts found. Click "Add Account" to create one.</p>
            </div>
            <div v-for="account in accounts.accounts" :key="account.id" class="table-row">
              <div class="col-account-name">{{ account.accountName || account.name }}</div>
              <div class="col-type">
                <span class="type-badge" :class="(account.accountType || account.type || '').toLowerCase()">
                  {{ account.accountType || account.type }}
                </span>
              </div>
              <div class="col-bank-no">
                <div v-if="account.bankName || account.bankInfo" class="bank-info">
                  <div class="bank-name">{{ account.bankName || account.bankInfo?.name }}</div>
                  <div class="bank-number">{{ account.accountNumber || account.bankInfo?.number }}</div>
                </div>
                <div v-else>-</div>
              </div>
              <div class="col-balance">{{ formatCurrency(account.balance || account.openingBalance || 0) }}</div>
              <div class="col-status">
                <span class="status-badge" :class="account.isActive !== false ? 'active' : 'inactive'">
                  {{ account.isActive !== false ? 'active' : 'inactive' }}
                </span>
              </div>
              <div class="col-actions">
                <button class="action-btn view-btn" title="View" @click="viewAccount(account)">&#x1F441;</button>
                <button class="action-btn edit-btn" title="Edit" @click="editAccount(account)">&#x270F;&#xFE0F;</button>
                <button class="action-btn delete-btn" title="Delete" @click="confirmDelete(account)">&#x1F5D1;&#xFE0F;</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Card View -->
        <div v-else class="mobile-cards">
          <div v-if="accounts.length === 0" class="empty-state">
            <p>No accounts found. Click "Add Account" to create one.</p>
          </div>
          <div v-for="account in accounts" :key="account.id" class="account-card">
            <div class="card-header">
              <div class="card-header-left">
                <span class="account-name">{{ account.accountName || account.name }}</span>
                <span class="type-badge" :class="(account.accountType || account.type || '').toLowerCase()">
                  {{ account.accountType || account.type }}
                </span>
              </div>
              <div class="card-actions">
                <button class="action-btn view-btn" title="View" @click="viewAccount(account)">&#x1F441;</button>
                <button class="action-btn edit-btn" title="Edit" @click="editAccount(account)">&#x270F;&#xFE0F;</button>
                <button class="action-btn delete-btn" title="Delete" @click="confirmDelete(account)">&#x1F5D1;&#xFE0F;</button>
              </div>
            </div>
            <div class="card-body">
              <div class="card-row" v-if="account.bankName || account.bankInfo">
                <span class="card-label">Bank</span>
                <span class="card-value">{{ account.bankName || account.bankInfo?.name }}</span>
              </div>
              <div class="card-row" v-if="account.accountNumber || account.bankInfo?.number">
                <span class="card-label">Account No.</span>
                <span class="card-value">{{ account.accountNumber || account.bankInfo?.number }}</span>
              </div>
              <div class="card-row">
                <span class="card-label">Balance</span>
                <span class="card-value balance">{{ formatCurrency(account.balance || account.openingBalance || 0) }}</span>
              </div>
              <div class="card-row">
                <span class="card-label">Status</span>
                <span class="status-badge" :class="account.isActive !== false ? 'active' : 'inactive'">
                  {{ account.isActive !== false ? 'active' : 'inactive' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Transactions Tab Content -->
      <div v-else class="transactions-section">
        <p>Transactions content coming soon...</p>
      </div>
    </div>

    <!-- Add/Edit Account Modal -->
    <AddAccountModal
      :isOpen="isModalOpen"
      :account="selectedAccount"
      :loading="loading"
      @close="closeModal"
      @create="handleCreateAccount"
      @update="handleUpdateAccount"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="confirm-modal">
        <h3>Delete Account</h3>
        <p>Are you sure you want to delete "{{ accountToDelete?.accountName || accountToDelete?.name }}"?</p>
        <div class="confirm-actions">
          <button class="btn-cancel" @click="cancelDelete">Cancel</button>
          <button class="btn-delete" @click="handleDelete" :disabled="loading">
            {{ loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AddAccountModal from '@/components/AddAccountModal.vue'

export default {
  name: 'CashBank',
  components: {
    AddAccountModal
  },
  data() {
    return {
      activeTab: 'Accounts',
      searchQuery: '',
      selectedFilter: '',
      isModalOpen: false,
      selectedAccount: null,
      showDeleteConfirm: false,
      accountToDelete: null,
      windowWidth: window.innerWidth,
      searchTimeout: null
    }
  },
  computed: {
    ...mapGetters('cashbank', ['accounts', 'stats', 'loading']),
    isMobileView() {
      return this.windowWidth < 768
    }
  },
  methods: {
    ...mapActions('cashbank', [
      'fetchAccounts',
      'fetchStats',
      'createAccount',
      'updateAccount',
      'deleteAccount',
      'clearCurrentAccount'
    ]),
    ...mapActions('accounting', ['addChartOfAccount']),

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0)
    },

    async loadData() {
      const params = {}
      if (this.searchQuery) {
        params.search = this.searchQuery
      }
      if (this.selectedFilter) {
        params.type = this.selectedFilter
      }
      await Promise.all([
        this.fetchAccounts(params),
        this.fetchStats()
      ])
    },

    handleSearch() {
      // Debounce search
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadData()
      }, 300)
    },

    handleFilterChange() {
      this.loadData()
    },

    openModal() {
      this.selectedAccount = null
      this.isModalOpen = true
    },

    closeModal() {
      this.isModalOpen = false
      this.selectedAccount = null
    },

    viewAccount(account) {
      // Could open a view modal or navigate to detail page
      console.log('View account:', account)
    },

    editAccount(account) {
      this.selectedAccount = account
      this.isModalOpen = true
    },

    confirmDelete(account) {
      this.accountToDelete = account
      this.showDeleteConfirm = true
    },

    cancelDelete() {
      this.accountToDelete = null
      this.showDeleteConfirm = false
    },

    async handleDelete() {
      if (!this.accountToDelete) return
      try {
        await this.deleteAccount(this.accountToDelete.id)
        this.cancelDelete()
      } catch (error) {
        console.error('Delete failed:', error)
      }
    },

    async handleCreateAccount(accountData) {
      try {
        const chartPayload = {
          name: accountData.accountName,
          description: '',
          parentID: accountData.accountType === 'Cash' ? 15 : 14,
          isTransactionAccount: 1
        }
        const chartResponse = await this.addChartOfAccount(chartPayload)
        const chartOfAccountID = chartResponse.payload?.responseData || chartResponse.payload?.id

        const payload = {
          accountName: accountData.accountName,
          accountType: accountData.accountType,
          bankName: accountData.bankName || null,
          branch: accountData.branch || null,
          accountNumber: accountData.accountNumber || null,
          openingBalance: parseFloat(accountData.openingBalance) || 0,
          chartOfAccountID: chartOfAccountID
        }
        await this.createAccount(payload)
        this.closeModal()
      } catch (error) {
        console.error('Create failed:', error)
      }
    },

    async handleUpdateAccount(accountData) {
      if (!this.selectedAccount) return
      try {
        const payload = {
          accountName: accountData.accountName,
          accountType: accountData.accountType,
          bankName: accountData.bankName || null,
          branch: accountData.branch || null,
          accountNumber: accountData.accountNumber || null,
          openingBalance: parseFloat(accountData.openingBalance) || 0
        }
        await this.updateAccount({
          id: this.selectedAccount.id,
          payload
        })
        this.closeModal()
      } catch (error) {
        console.error('Update failed:', error)
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    }
  },
  created() {
    this.loadData()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    clearTimeout(this.searchTimeout)
  }
}
</script>

<style scoped>
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 48px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-description {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 400;
}

/* Add Account Button */
.add-account-button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
  white-space: nowrap;
}

.add-account-button:hover {
  background: #1d4ed8;
}

.plus-icon {
  font-size: 18px;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 48px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.stat-label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}

.stat-icon {
  font-size: 20px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
}

.stat-card.total-balance .stat-value {
  color: #10b981;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin-top: 16px;
  color: #6b7280;
}

/* Empty State */
.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

/* Tabs Section */
.tabs-section {
  display: flex;
  gap: 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid #e5e7eb;
}

.tab-button {
  background: none;
  border: none;
  padding: 12px 0;
  font-size: 15px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
  position: relative;
  bottom: -1px;
}

.tab-button:hover {
  color: #1f2937;
}

.tab-button.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

/* Search and Filter */
.search-filter-section {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
}

.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  gap: 12px;
}

.search-icon {
  font-size: 18px;
  color: #9ca3af;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1f2937;
}

.search-input::placeholder {
  color: #9ca3af;
}

.filter-dropdown {
  width: 150px;
}

.filter-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  color: #1f2937;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236b7280' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

/* Accounts Section */
.accounts-section {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.accounts-header {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.accounts-table {
  overflow-x: auto;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1fr 1fr 1.5fr;
  gap: 16px;
  padding: 16px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 1fr 1fr 1.5fr;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
  transition: background 0.2s ease;
}

.table-row:hover {
  background: #f9fafb;
}

.col-account-name {
  font-weight: 600;
  color: #1f2937;
}

.col-type {
  font-size: 14px;
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: #e0e7ff;
  color: #4f46e5;
}

.type-badge.bank {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.cash {
  background: #dcfce7;
  color: #166534;
}

.col-bank-no {
  font-size: 14px;
  color: #6b7280;
}

.bank-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bank-name {
  font-weight: 500;
  color: #1f2937;
}

.bank-number {
  font-size: 12px;
  color: #9ca3af;
}

.col-balance {
  font-weight: 600;
  color: #1f2937;
}

.col-status {
  font-size: 14px;
}

.status-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: #2563eb;
  color: white;
}

.status-badge.inactive {
  background: #ef4444;
}

.col-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  padding: 4px 8px;
}

.action-btn:hover {
  opacity: 1;
}

.delete-btn:hover {
  color: #ef4444;
}

/* Mobile Cards */
.mobile-cards {
  padding: 16px;
}

.account-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.account-card:last-child {
  margin-bottom: 0;
}

.account-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 8px;
}

.account-card .card-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.account-card .account-name {
  font-weight: 600;
  color: #1f2937;
}

.account-card .card-actions {
  display: flex;
  gap: 8px;
}

.account-card .card-body {
  padding: 16px;
}

.account-card .card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.account-card .card-row:last-child {
  border-bottom: none;
}

.account-card .card-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.account-card .card-value {
  font-size: 14px;
  color: #1f2937;
}

.account-card .card-value.balance {
  font-weight: 600;
}

/* Delete Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.confirm-modal h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.confirm-modal p {
  color: #6b7280;
  margin-bottom: 24px;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel {
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f3f4f6;
}

.btn-delete {
  padding: 10px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-delete:hover {
  background: #dc2626;
}

.btn-delete:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 16px;
  }

  .page-title {
    font-size: 28px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .search-filter-section {
    flex-direction: column;
  }

  .filter-dropdown {
    width: 100%;
  }

  .table-header {
    display: none;
  }
}
</style>
